import { endpoints } from 'core/api/endpoints';
import { apiDelete, apiDownload, apiGet, apiPatch, apiPost, apiPut, apiUpload } from 'core/api/client';
import { ensureExpectedRevision, getExpectedRevision, withExpectedRevision } from 'core/api/revisionStore';
import { datedWorkbookFilename, resolveDownloadFilename } from 'features/workbooks/workbook';
import { fetchResultItemCatalog, normalizeResultItemCatalog } from 'features/result-content/resultItemCatalog';
import { isValidVersionId, logTopThreeDiagnostics } from 'features/top-three-profiles/topThreeProfiles';
import { serializeFilters } from 'shared/utilities/format';
import { ApiError } from 'core/errors/ApiError';

function requireVersionId(versionId) {
  if (!isValidVersionId(versionId)) {
    throw new ApiError({
      status: 0,
      message: 'A valid assessment version is required.',
    });
  }
  return String(versionId);
}

async function withTopThreeDiagnostics(method, url, versionId, runner) {
  try {
    return await runner();
  } catch (error) {
    logTopThreeDiagnostics({
      method,
      url,
      versionId,
      status: error?.status,
      code: error?.code || error?.payload?.code,
    });
    throw error;
  }
}

export async function ensureRevision() {
  return ensureExpectedRevision(() => apiGet(endpoints.overview));
}

async function mutationBody(body = {}) {
  await ensureRevision();
  return withExpectedRevision(body);
}

async function mutationParams(params = {}) {
  await ensureExpectedRevision(() => apiGet(endpoints.overview));
  return { ...params, expected_revision: getExpectedRevision() };
}

export const assessmentApi = {
  overview(config) {
    return apiGet(endpoints.overview, config);
  },
  listPatterns(config) {
    return apiGet(endpoints.patterns, config);
  },
  showPattern(id, config) {
    return apiGet(endpoints.pattern(id), config);
  },
  async createPattern(body) {
    if (typeof FormData !== 'undefined' && body instanceof FormData) {
      await ensureRevision();
      body.append('expected_revision', String(getExpectedRevision()));
      return apiPost(endpoints.patterns, body);
    }
    return apiPost(endpoints.patterns, await mutationBody(body));
  },
  async updatePattern(id, body) {
    if (typeof FormData !== 'undefined' && body instanceof FormData) {
      await ensureRevision();
      body.append('expected_revision', String(getExpectedRevision()));
      return apiPatch(endpoints.pattern(id), body);
    }
    return apiPatch(endpoints.pattern(id), await mutationBody(body));
  },
  async setPatternActivation(id, isActive) {
    return apiPatch(endpoints.patternActivation(id), await mutationBody({ is_active: isActive }));
  },
  async deletePattern(id) {
    return apiDelete(endpoints.pattern(id), { params: await mutationParams() });
  },
  weightMatrix(id, sectionId, config) {
    return apiGet(endpoints.patternWeightMatrix(id), {
      ...config,
      params: sectionId ? { section_id: sectionId } : undefined,
    });
  },
  async saveWeightMatrix(id, weights) {
    return apiPut(endpoints.patternWeightMatrix(id), await mutationBody({ weights }));
  },
  listSections(config) {
    return apiGet(endpoints.sections, config);
  },
  async createSection(body) {
    return apiPost(endpoints.sections, await mutationBody(body));
  },
  async updateSection(id, body) {
    return apiPatch(endpoints.section(id), await mutationBody(body));
  },
  async deleteSection(id) {
    return apiDelete(endpoints.section(id), { params: await mutationParams() });
  },
  async reorderSections(sectionIds) {
    return apiPatch(endpoints.sectionsReorder, await mutationBody({ section_ids: sectionIds }));
  },
  listQuestions(sectionId, config) {
    return apiGet(endpoints.questions, {
      ...config,
      params: sectionId ? { section_id: sectionId } : undefined,
    });
  },
  showQuestion(id, config) {
    return apiGet(endpoints.question(id), config);
  },
  async createQuestion(body) {
    return apiPost(endpoints.questions, await mutationBody(body));
  },
  async updateQuestion(id, body) {
    return apiPatch(endpoints.question(id), await mutationBody(body));
  },
  async replaceQuestionConfiguration(id, body) {
    return apiPut(endpoints.questionConfiguration(id), await mutationBody(body));
  },
  async setQuestionActivation(id, isActive) {
    return apiPatch(endpoints.questionActivation(id), await mutationBody({ is_active: isActive }));
  },
  async deleteQuestion(id) {
    return apiDelete(endpoints.question(id), { params: await mutationParams() });
  },
  async reorderQuestions(questions) {
    return apiPatch(endpoints.questionsReorder, await mutationBody({ questions }));
  },
  showAnswer(id, config) {
    return apiGet(endpoints.answer(id), config);
  },
  async saveAnswerWeights(id, weights) {
    return apiPut(endpoints.answerWeights(id), await mutationBody({ weights }));
  },
  listVersions(config) {
    return apiGet(endpoints.versions, config);
  },
  showVersion(id, config) {
    return apiGet(endpoints.version(id), config);
  },
  preflight(body = {}) {
    return apiPost(endpoints.versionsPreflight, body);
  },
  async buildVersion(body) {
    return apiPost(endpoints.versionsBuild, await mutationBody(body));
  },
  publishVersion(id) {
    return apiPost(endpoints.versionPublish(id), {});
  },
  acknowledgeVersionZeroWeights(id, body = { acknowledge_zero_weights: true }) {
    return apiPost(endpoints.versionAcknowledgeZeroWeights(id), body);
  },
  async listTopThreeProfiles(versionId, filters = {}, config) {
    const id = requireVersionId(versionId);
    const params = { ...filters };
    delete params.version_id;
    const url = endpoints.versionTopThreeProfiles(id);
    return withTopThreeDiagnostics('GET', url, id, () => (
      apiGet(url, { ...config, params: serializeFilters(params) })
    ));
  },
  async getTopThreeProfile(versionId, id, config) {
    const resolvedVersionId = requireVersionId(versionId);
    const url = endpoints.versionTopThreeProfile(resolvedVersionId, id);
    return withTopThreeDiagnostics('GET', url, resolvedVersionId, () => apiGet(url, config));
  },
  async updateTopThreeProfile(versionId, id, body) {
    const resolvedVersionId = requireVersionId(versionId);
    return apiPatch(endpoints.versionTopThreeProfile(resolvedVersionId, id), body);
  },
  async getTopThreeProfileCoverage(versionId, config) {
    const id = requireVersionId(versionId);
    const url = endpoints.versionTopThreeProfileCoverage(id);
    return withTopThreeDiagnostics('GET', url, id, () => apiGet(url, config));
  },
  async repairTopThreeProfiles(versionId) {
    const id = requireVersionId(versionId);
    const url = endpoints.versionTopThreeProfilesRepair(id);
    return withTopThreeDiagnostics('POST', url, id, () => apiPost(url, {}));
  },
  listTags(config) {
    return apiGet(endpoints.tags, config);
  },
  showTag(id, config) {
    return apiGet(endpoints.tag(id), config);
  },
  createTag(body) {
    return apiPost(endpoints.tags, body);
  },
  updateTag(id, body) {
    return apiPatch(endpoints.tag(id), body);
  },
  setTagActivation(id, isActive) {
    return apiPatch(endpoints.tagActivation(id), { is_active: isActive });
  },
  deleteTag(id) {
    return apiDelete(endpoints.tag(id));
  },
  listPersonalSummaries(filters, config) {
    return apiGet(endpoints.personalSummaries, { ...config, params: serializeFilters(filters) });
  },
  showPersonalSummary(id, config) {
    return apiGet(endpoints.personalSummary(id), config);
  },
  createPersonalSummary(body) {
    return apiPost(endpoints.personalSummaries, body);
  },
  updatePersonalSummary(id, body) {
    return apiPatch(endpoints.personalSummary(id), body);
  },
  setPersonalSummaryActivation(id, isActive) {
    return apiPatch(endpoints.personalSummaryActivation(id), { is_active: isActive });
  },
  deletePersonalSummary(id) {
    return apiDelete(endpoints.personalSummary(id));
  },
  async listResultItems(filters, config) {
    const payload = await apiGet(endpoints.resultItems, { ...config, params: serializeFilters(filters) });
    return normalizeResultItemCatalog(payload);
  },
  listResultItemCatalog(filters, config) {
    return fetchResultItemCatalog(
      (params, requestConfig) => assessmentApi.listResultItems(params, requestConfig),
      filters,
      config,
    );
  },
  showResultItem(id, config) {
    return apiGet(endpoints.resultItem(id), config);
  },
  createResultItem(body) {
    return apiPost(endpoints.resultItems, body);
  },
  updateResultItem(id, body) {
    return apiPatch(endpoints.resultItem(id), body);
  },
  setResultItemActivation(id, isActive) {
    return apiPatch(endpoints.resultItemActivation(id), { is_active: isActive });
  },
  deleteResultItem(id) {
    return apiDelete(endpoints.resultItem(id));
  },
  listAttempts(filters, config) {
    return apiGet(endpoints.attempts, { ...config, params: serializeFilters(filters) });
  },
  showAttempt(id, config) {
    return apiGet(endpoints.attempt(id), config);
  },
  downloadQuestionnaireTemplate(config) {
    return downloadWorkbook(endpoints.questionnaireTemplate, datedWorkbookFilename('questionnaire-template'), config);
  },
  exportQuestionnaire(config) {
    return downloadWorkbook(endpoints.questionnaireExport, datedWorkbookFilename('questionnaire-export'), config);
  },
  async previewQuestionnaireImport(file, config) {
    await ensureRevision();
    return apiUpload(endpoints.questionnaireImportPreview, workbookForm(file), config);
  },
  async commitQuestionnaireImport(file, { fileSha256, confirmDestructive, expectedRevision } = {}, config) {
    await ensureRevision();
    return apiUpload(endpoints.questionnaireImportCommit, workbookForm(file, { fileSha256, confirmDestructive, expectedRevision }), config);
  },
  downloadNewQuestionsTemplate(config) {
    return downloadWorkbook(
      endpoints.newQuestionsTemplate,
      datedWorkbookFilename('pacilo-new-questions-template'),
      config,
    );
  },
  previewNewQuestionsImport(file, config) {
    return apiUpload(endpoints.newQuestionsImportPreview, workbookForm(file), config);
  },
  commitNewQuestionsImport(file, commitToken, config) {
    const body = new FormData();
    if (file) {
      const filename = typeof file.name === 'string' && file.name.trim() !== '' ? file.name : 'workbook.xlsx';
      body.append('file', file, filename);
    }
    body.append('commit_token', String(commitToken));
    return apiUpload(endpoints.newQuestionsImportCommit, body, config);
  },
  async downloadTopThreeProfileTemplate(versionId, config) {
    const id = requireVersionId(versionId);
    return downloadWorkbook(
      endpoints.versionTopThreeProfilesTemplate(id),
      datedWorkbookFilename(`top-three-profiles-template-v${id}`),
      config,
    );
  },
  async exportTopThreeProfiles(versionId, config) {
    const id = requireVersionId(versionId);
    return downloadWorkbook(
      endpoints.versionTopThreeProfilesExport(id),
      datedWorkbookFilename(`top-three-profiles-v${id}`),
      config,
    );
  },
  async previewTopThreeProfileWorkbook(versionId, file, config) {
    const id = requireVersionId(versionId);
    await ensureRevision();
    return apiUpload(endpoints.versionTopThreeProfilesImportPreview(id), workbookForm(file), config);
  },
  async commitTopThreeProfileWorkbook(versionId, file, {
    commitToken,
    previewToken,
    fileSha256,
    expectedRevision,
  } = {}, config) {
    const id = requireVersionId(versionId);
    await ensureRevision();
    const body = workbookForm(file);
    const token = commitToken || previewToken;
    if (token) {
      body.append('commit_token', String(token));
    }
    if (fileSha256) {
      body.append('file_sha256', String(fileSha256));
    }
    const revision = expectedRevision ?? getExpectedRevision();
    if (revision != null && revision !== '') {
      body.append('expected_revision', String(revision));
    }
    return apiUpload(endpoints.versionTopThreeProfilesImportCommit(id), body, config);
  },
};

async function downloadWorkbook(url, fallbackFilename, config) {
  const result = await apiDownload(url, config);
  return {
    blob: result.blob,
    filename: resolveDownloadFilename(result.filenameHeader, fallbackFilename),
  };
}

export function buildPatternFormData(fields, iconFile) {
  const body = new FormData();
  Object.entries(fields).forEach(([key, value]) => {
    if (value === undefined || value === null || key === 'icon') {
      return;
    }
    if (typeof value === 'boolean') {
      body.append(key, value ? '1' : '0');
      return;
    }
    body.append(key, String(value));
  });
  if (iconFile) {
    body.append('icon', iconFile);
  }
  return body;
}

function workbookForm(file, extras = {}) {
  const body = new FormData();
  if (file) {
    const filename = typeof file.name === 'string' && file.name.trim() !== '' ? file.name : 'workbook.xlsx';
    body.append('file', file, filename);
  }
  if (extras.fileSha256) {
    body.append('file_sha256', extras.fileSha256);
    const revision = extras.expectedRevision ?? getExpectedRevision();
    if (revision != null && revision !== '') {
      body.append('expected_revision', String(revision));
    }
    if (extras.confirmDestructive) {
      body.append('confirm_destructive', '1');
    }
  }
  return body;
}
